import { useState } from 'react'
import { useIsMutating, useQuery } from '@tanstack/react-query'
import type { inferOutput } from '@trpc/tanstack-react-query'
import { Alert, Button, Empty, Skeleton, Tag } from 'antd'
import { FormPageContainer } from '@/components/form/form-page-container.tsx'
import { WorkspaceHeader } from '@/components/workspace-header.tsx'
import { useTRPC } from '@/trpc/react.ts'
import { cn } from '@/utils/index.ts'
import { formatAmount } from '@/utils/money.ts'
import type { GroupBatchTarget } from './group-batch-dialog.tsx'
import { GroupReceiptPanel } from './group-receipt-panel.tsx'
import { toCents } from './utils/money.ts'

export function GroupReceiptWorkspace({ target }: { target: GroupBatchTarget }) {
  const trpc = useTRPC()
  const query = useQuery({
    ...trpc.bill.groupBatchTargets.queryOptions(target),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  })
  if (!query.data) {
    return (
      <FormPageContainer title="批量登记收款" breadcrumbRender={false}>
        {query.error ? (
          <Alert
            type="error"
            title={query.error.message}
            action={<Button onClick={() => void query.refetch()}>重试</Button>}
          />
        ) : (
          <Skeleton active />
        )}
      </FormPageContainer>
    )
  }
  return <GroupReceiptSession initialGroups={query.data} />
}

function GroupReceiptSession({
  initialGroups
}: {
  initialGroups: inferOutput<ReturnType<typeof useTRPC>['bill']['groupBatchTargets']>
}) {
  const trpc = useTRPC()
  // 搜索命中的集合在进入工作台时固定，确认出账或收款后不会从当前会话移出。
  const [snapshot] = useState(initialGroups)
  const query = useQuery({
    ...trpc.bill.groupBatchTargets.queryOptions({ mode: 'ids', ids: snapshot.map((group) => group.id) }),
    enabled: snapshot.length > 0,
    initialData: snapshot,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  })
  const busy = useIsMutating({ mutationKey: trpc.bill.groupBatchAction.mutationOptions().mutationKey }) > 0
  const [activeId, setActiveId] = useState<number>()
  const [visited, setVisited] = useState<number[]>([])
  const freshById = new Map(query.data.map((group) => [group.id, group]))
  const groups = snapshot.map((group) => freshById.get(group.id) ?? group)
  const groupById = new Map(groups.map((group) => [group.id, group]))
  const selectedId = activeId ?? groups[0]?.id
  const mountedIds = [...new Set([...visited, ...(selectedId ? [selectedId] : [])])]
  return (
    <div className="bg-page flex h-dvh min-h-0 flex-col overflow-hidden">
      <WorkspaceHeader>
        <div>
          <h1 className="m-0 text-base font-semibold">批量登记收款</h1>
          <span className="text-content-muted text-xs">{groups.length} 个账单组 · 逐组登记 · 切换时保留未提交内容</span>
        </div>
      </WorkspaceHeader>
      <FormPageContainer
        title={false}
        breadcrumbRender={false}
        className="flex min-h-0 flex-1 flex-col [&_.ant-pro-grid-content]:contents [&_.ant-pro-grid-content-children]:contents [&_.ant-pro-page-container-children-container]:flex [&_.ant-pro-page-container-children-container]:min-h-0 [&_.ant-pro-page-container-children-container]:flex-1 [&_.ant-pro-page-container-children-container]:flex-col"
      >
        {query.error && (
          <Alert
            type="error"
            title={query.error.message}
            action={<Button onClick={() => void query.refetch()}>重试</Button>}
          />
        )}
        {query.isSuccess && !groups.length && <Empty description="没有符合条件的账单组" />}
        {!!groups.length && (
          <main className="grid min-h-0 flex-1 grid-rows-[auto_minmax(0,1fr)] gap-3 pb-3 lg:grid-cols-[18rem_minmax(0,1fr)] lg:grid-rows-1">
            <nav aria-label="收款账单组" className="max-h-56 min-w-0 space-y-3 overflow-auto lg:max-h-none">
              {groups.map((group) => {
                let status = '待登记收款'
                if (group.status === 'voided') status = '已作废'
                else if (group.status === 'draft') status = '草稿 · 待确认出账'
                else if (toCents(group.outstandingAmount) <= BigInt(0)) status = '全部已结算'
                return (
                  <button
                    key={group.id}
                    type="button"
                    disabled={busy}
                    aria-current={group.id === selectedId ? 'true' : undefined}
                    onClick={() => {
                      setVisited(mountedIds)
                      setActiveId(group.id)
                    }}
                    className={cn(
                      'bg-surface border-border flex w-full cursor-pointer flex-col gap-2 rounded-xl border p-4 text-left disabled:cursor-not-allowed disabled:opacity-60',
                      group.id === selectedId && 'border-accent/50 bg-accent/10'
                    )}
                  >
                    <span className="text-content-muted text-xs">账单组 #{group.id}</span>
                    <span className="text-sm font-semibold">{group.groupNo}</span>
                    <span className="text-content-muted text-xs">{group.customerName}</span>
                    <span className="text-sm">待收 ¥{formatAmount(group.outstandingAmount)}</span>
                    <Tag className="self-start">{status}</Tag>
                  </button>
                )
              })}
            </nav>
            <section
              aria-label="登记收款表单"
              className="bg-surface border-border min-h-0 min-w-0 overflow-hidden rounded-xl border"
            >
              {mountedIds.map((id) => (
                <div
                  key={id}
                  hidden={id !== selectedId}
                  className={cn('h-full min-h-0 flex-col overflow-auto', id === selectedId ? 'flex' : 'hidden')}
                >
                  <header className="border-border shrink-0 border-b px-6 py-4">
                    <h2 className="m-0 text-base font-semibold">{groupById.get(id)?.groupNo}</h2>
                  </header>
                  <div className="min-h-0 flex-1">
                    <GroupReceiptPanel groupId={id} />
                  </div>
                </div>
              ))}
            </section>
          </main>
        )}
      </FormPageContainer>
    </div>
  )
}
